import { ApolloClient, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { createUploadLink } from 'apollo-upload-client'
import cache, { appErrorVar } from 'lib/utils/store/cache'
import typeDefs from 'lib/utils/store/typeDefs'
import queryString from 'query-string'
import cookies from 'lib/cookies'
import AppError, { ERROR_DISPLAY_METHOD, ERRORS, logException } from 'lib/errors'
import { credentialsSetting, dataURI, refreshURI } from 'lib/constants'
import { isDev } from 'lib/env'
import { setApolloErrorBreadcrumb } from 'lib/sentry'
import lscache from 'lscache'
import { logAuthUserActivity } from 'lib/logAuthUserActivity'
import { handleVerticalReferrer, tryUpdateVerticalReferrer } from 'lib/verticalReferrer'
import { getCurrentHub } from '@sentry/react'
import { clearAuth0UserSession } from 'lib/helpers'

import possibleTypes from 'possibleTypes.json'

try { lscache.flushExpired() } catch (e) { console.error(e) }

// Cleanup some login related cookies in case user just logged in/out successfully.
// Required cookies will be set in Client.js or login / logout callback handler.
if ([
    '/login/callback',
    '/logout/callback',
    '/login/sso/callback/success',
    '/login/sso/callback/failed',
    '/logout/sso/callback/success',
    '/logout/sso/callback/failed',
].includes(window.location.pathname)) {
    // Cleanup some cookies that will be reset based on the actual status
    cookies.remove('userLoggedIn')
    cookies.remove('sessionExpired')
    cookies.remove('alma-token')
}

if ([
    '/logout/sso/callback/success'
].includes(window.location.pathname)) {
    // End the client session by removing cookie
    // This will cause all the tabs with this client session to refresh
    cookies.remove('clientSession')
}

const setNewSessionCookie = () => {
    const clientSession = { id: Date.now() }
    console.log('Create and set new session cookie:', clientSession.id)
    cookies.set('clientSession', clientSession)
    return cookies.get('clientSession') // use cookies.get to confirm cookie was actually written
}

console.log('Existing client session:', cookies.get('clientSession'))
const clientSession = cookies.get('clientSession') || setNewSessionCookie()

try {
    handleVerticalReferrer()
} catch (e) {
    console.error(e)
}

if (clientSession) {
    // Check if client session changes. If so, we want to reload the page.
    // In case user logs out in another browser window or tab, we want to do the reload for all other tabs.
    const sessionChangeCheckInterval = setInterval(() => {
        const cs = cookies.get('clientSession')
        if (!cs || cs.id !== clientSession.id) {
            clearInterval(sessionChangeCheckInterval)
            console.log('Forced reload due to client session id mismatch...')
            window.location.reload(true)
        }
    }, 1000)
}

const signerLink = setContext((_, { headers }) => {
    const parsedSearch = queryString.parse(window.location.search)
    const checksum = parsedSearch.checksum

    if (checksum) {
        return {
            headers: {
                ...headers,
                'X-checksum': checksum,
            }
        }
    }

    return headers
})

const refreshLink = new RetryLink({
    delay: {
        initial: 0,
    },
    attempts: async (count, operation, error) => {
        const currentUrl = new URL(window.location.href)

        // Check if the refresh has already been attempted
        if (currentUrl.searchParams.has('refreshSuccess') || currentUrl.searchParams.has('refreshFailed')) {
            console.log('Refresh already attempted, avoiding loop')
            cookies.set('refreshAttempted', 1, { maxAge: 120 })

            if (currentUrl.searchParams.has('refreshSuccess')) {
                // Login successful
                cookies.set('userLoggedIn', 1)
                logAuthUserActivity()
                tryUpdateVerticalReferrer()
            }

            // Remove 'refreshSuccess' and 'refreshFailed' from the URL
            currentUrl.searchParams.delete('refreshSuccess')
            currentUrl.searchParams.delete('refreshFailed')

            // Push the updated URL to the browser's history without reloading the page
            window.history.replaceState({}, document.title, currentUrl.toString())

            return false // Stop retrying to avoid a loop
        }

        if (error.statusCode === 401) {
            clearAuth0UserSession()

            if (cookies.get('refreshAttempted')) {
                console.log('Refresh done, not retrying')
                cookies.remove('refreshAttempted')

                return false
            }

            console.log('Trying to refresh the token')

            // Prepare success and failure URLs based on the current URL
            const successUrl = new URL(currentUrl)
            const failureUrl = new URL(currentUrl)

            successUrl.searchParams.set('refreshSuccess', 'true')
            failureUrl.searchParams.set('refreshFailed', 'true')

            // Construct the refresh URL
            const refreshURL = `${refreshURI}?successUrl=${encodeURIComponent(successUrl.toString())}&failureUrl=${encodeURIComponent(failureUrl.toString())}`

            console.log('Redirecting to refresh URL:', refreshURL)

            // Redirect the user to the refresh URL
            window.location.href = refreshURL

            // Since the page will redirect, stop further retries
            return false
        }

        // Non-401 errors will not trigger a retry
        return false
    }
})

const errorLink = onError(({ response, operation, graphQLErrors, networkError }) => {
    const context = operation && operation.getContext()

    isDev() && console.error('[Errored operation]:', operation, 'context:', context, 'response:', response)

    if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
            isDev() && console.error(`[GraphQL error]: ${error}`)
        })
    }

    if (networkError) {
        isDev() && console.error('[Network error]:', networkError)

        if (networkError.statusCode === 401) {
            if (cookies.get('userLoggedIn')) {
                console.log('User session expired')
                cookies.remove('userLoggedIn')
                cookies.set('sessionExpired', 1)
                appErrorVar(ERRORS.SESSION_EXPIRED)
            }
        } else {
            const appError = AppError.readFromApolloError(networkError)
            if (appError && appError.displayMethod === ERROR_DISPLAY_METHOD.NOTIFY) {
                appErrorVar(appError)
            }
        }
    }

    try {
        setApolloErrorBreadcrumb(response, operation, graphQLErrors, networkError)
    } catch (error) {
        logException(error)
    }
})

const extractOperationTypeAndName = (operation) => {
    const { operationName, query } = operation
    // Add operation name to the uri, so that it's easier to read network logs and Sentry tracing.
    // m = operation is mutation, q = operation is query
    const operationType = (query?.definitions[0]?.operation === 'mutation') ? 'm' : 'q'
    return { operationName, operationType }
}

const uploadLink = createUploadLink({
    uri: (operation) => {
        const { operationName, operationType } = extractOperationTypeAndName(operation)
        return dataURI + `?${operationType}=${operationName}`
    },
    credentials: credentialsSetting,
})

const logAuthUserActivityLink = new ApolloLink((operation, forward) => {
    logAuthUserActivity()

    return forward(operation)
})

const sentryTransactionLink = new ApolloLink((operation, forward) => {
    const { operationName, operationType } = extractOperationTypeAndName(operation)

    const transaction = getCurrentHub()
        .getScope()
        .getTransaction()

    if (transaction) {
        const span = transaction.startChild({ op: `gql.${operationType}`, description: operationName })

        return forward(operation).map(response => {
            span.finish()

            return response
        })
    }

    return forward(operation)
})

const Client = new ApolloClient({
    link: ApolloLink.from([
        logAuthUserActivityLink,
        signerLink,
        errorLink,
        refreshLink,
        sentryTransactionLink,
        uploadLink,
    ]),
    connectToDevTools: true,
    cache,
    typeDefs,
    possibleTypes,
})

export default Client
